// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 34-workloads.md
// @greenlight-spec: 19-design-system.md
import * as React from 'react';
import type { z } from 'zod';

import { MonoValue } from '@/components/greenlight/MonoValue';
import { relativeTime } from '@/components/greenlight/relativeTime';
import { StatusBadge, type StatusBadgeVariant } from '@/components/greenlight/StatusBadge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AD_CARD } from '@/components/greenlight/surfaces';
import { cn } from '@/lib/utils';
import type { GetAppResultSchema } from '@greenlight/shared';

import { WorkloadRow } from './WorkloadRows';

type GetAppResult = z.infer<typeof GetAppResultSchema>;
type Workload = GetAppResult['workloads'][number];
type Resource = GetAppResult['resources'][number];
type Grant = GetAppResult['grants'][number];

// ── Display names ────────────────────────────────────────────────────────────

/** "nightly-export" → "Nightly export". */
function humanize(s: string): string {
  const spaced = s.replace(/[-_]+/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

const WORKLOAD_KIND_LABEL: Record<string, string> = {
  web: 'Web service',
  deployment: 'Web service',
  server: 'Web service',
  cron: 'Scheduled job',
};

const RESOURCE_KIND_LABEL: Record<string, string> = {
  postgres: 'PostgreSQL',
  postgresql: 'PostgreSQL',
  mysql: 'MySQL',
  blob: 'Blob storage',
  storage: 'Blob storage',
  redis: 'Redis',
};

const INTEGRATION_LABEL: Record<string, string> = {
  github: 'GitHub',
  salesforce: 'Salesforce',
  'salesforce-prod': 'Salesforce',
  snowflake: 'Snowflake',
  'snowflake-analytics': 'Snowflake',
  hubspot: 'HubSpot',
};

// ── Status presentation ──────────────────────────────────────────────────────

const GREEN = new Set(['ready', 'active', 'running', 'granted']);
const RED = new Set(['failed', 'denied', 'revoked']);

function statusTone(status: string): 'green' | 'red' | 'amber' {
  if (GREEN.has(status)) return 'green';
  if (RED.has(status)) return 'red';
  return 'amber';
}

const DOT_FILL: Record<'green' | 'red' | 'amber', string> = {
  green: 'fill-[hsl(var(--status-green))]',
  red: 'fill-[hsl(var(--status-red))]',
  amber: 'fill-[hsl(var(--status-amber))]',
};

const BADGE_VARIANT: Record<'green' | 'red' | 'amber', StatusBadgeVariant> = {
  green: 'active',
  red: 'failed',
  amber: 'pending',
};

// ── SVG layout ───────────────────────────────────────────────────────────────
// Three labeled regions: platform resources on the left, the app's workloads in
// the middle, granted integrations on the right. Edges run outward from each
// workload to everything it can reach. Resources and grants are app-level in
// the projection (not attributed to a specific workload), so every workload
// connects to every side node; at MVP scale (1–2 workloads) that reads
// truthfully. Everything the page needs to say about a node — friendly name,
// status, a cron's last run — lives inside its box.

const NODE_W = 172;
const NODE_H = 46;
const NODE_GAP = 12;
const COL_GAP = 34;
const REGION_PAD = 8;
const LABEL_H = 24;

const LEFT_X = REGION_PAD + 2;
const MID_X = LEFT_X + NODE_W + COL_GAP;
const RIGHT_X = MID_X + NODE_W + COL_GAP;
const VIEW_W = RIGHT_X + NODE_W + REGION_PAD + 2;

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

type NodeShape = 'workload' | 'database' | 'resource' | 'integration';

interface NodeDef {
  key: string;
  title: string;
  subtitle: string;
  status: string;
  shape: NodeShape;
}

const DB_KIND = /postgres|mysql|maria|sql|database|db/i;

function workloadNode(w: Workload): NodeDef {
  const kindLabel = WORKLOAD_KIND_LABEL[w.kind] ?? humanize(w.kind);
  const subtitle =
    w.kind === 'cron'
      ? `${w.status}${w.last_run_at ? ` · ran ${relativeTime(w.last_run_at)}` : ''}`
      : `${kindLabel} · ${w.status}`;
  return {
    key: `w-${w.name}`,
    title: humanize(w.name),
    subtitle,
    status: w.status,
    shape: 'workload',
  };
}

function resourceNode(r: Resource): NodeDef {
  return {
    key: `r-${r.name}`,
    title: RESOURCE_KIND_LABEL[r.kind.toLowerCase()] ?? humanize(r.kind),
    subtitle: `${r.name} · ${r.status}`,
    status: r.status,
    shape: DB_KIND.test(r.kind) ? 'database' : 'resource',
  };
}

function grantNode(g: Grant): NodeDef {
  return {
    key: `g-${g.integration}-${g.credential}`,
    title: INTEGRATION_LABEL[g.integration.toLowerCase()] ?? humanize(g.integration),
    subtitle: `${g.credential} · ${g.status}`,
    status: g.status,
    shape: 'integration',
  };
}

// Node strokes lean on --muted-foreground rather than --border: the border
// token is tuned for hairlines on large surfaces and washes out at these small
// SVG sizes in both themes.
const SHAPE_FILL: Record<NodeShape, string> = {
  workload: 'fill-[hsl(var(--card))] stroke-[hsl(var(--slate)/0.55)]',
  database: 'fill-[hsl(var(--muted))] stroke-[hsl(var(--slate)/0.55)]',
  resource: 'fill-[hsl(var(--muted))] stroke-[hsl(var(--slate)/0.55)]',
  integration: 'fill-[hsl(var(--primary)/0.1)] stroke-[hsl(var(--primary)/0.6)]',
};

// Vertical radius of the cylinder caps; the body is inset by this much top and
// bottom so the full shape stays inside NODE_H. Kept shallow so the top rim
// stays clear of the title text baseline.
const CAP_RY = 5;

function CylinderShape({ x, y }: { x: number; y: number }): React.JSX.Element {
  const rx = NODE_W / 2;
  const top = y + CAP_RY;
  const bottom = y + NODE_H - CAP_RY;
  return (
    <g className={SHAPE_FILL.database} strokeWidth={1.25}>
      {/* body with the bottom cap bulging down */}
      <path
        d={`M ${x} ${top} V ${bottom} A ${rx} ${CAP_RY} 0 0 0 ${x + NODE_W} ${bottom} V ${top}`}
      />
      {/* top cap as a full ellipse so the rim reads as a lid */}
      <ellipse cx={x + rx} cy={top} rx={rx} ry={CAP_RY} />
    </g>
  );
}

function GraphNode({ node, x, y }: { node: NodeDef; x: number; y: number }): React.JSX.Element {
  const tone = statusTone(node.status);
  const isCylinder = node.shape === 'database';
  // A healthy workload is the live heart of the graph: green border plus a
  // softly pulsing beacon ring. animate-pulse only varies opacity (no layout
  // motion) and motion-reduce disables it entirely.
  const isLiveWorkload = node.shape === 'workload' && tone === 'green';
  return (
    <g>
      {isLiveWorkload && (
        <rect
          x={x - 3}
          y={y - 3}
          width={NODE_W + 6}
          height={NODE_H + 6}
          rx={9}
          fill="none"
          className="animate-pulse stroke-[hsl(var(--status-green)/0.45)] motion-reduce:animate-none"
          strokeWidth={2}
          data-testid="workload-beacon"
        />
      )}
      {isCylinder ? (
        <CylinderShape x={x} y={y} />
      ) : (
        <rect
          x={x}
          y={y}
          width={NODE_W}
          height={NODE_H}
          rx={node.shape === 'integration' ? 10 : 6}
          className={
            isLiveWorkload
              ? 'fill-[hsl(var(--card))] stroke-[hsl(var(--status-green)/0.75)]'
              : SHAPE_FILL[node.shape]
          }
          strokeWidth={isLiveWorkload ? 1.5 : 1.25}
        />
      )}
      <text
        x={x + 10}
        y={y + (isCylinder ? 23 : 19)}
        className="fill-foreground text-[11px] font-medium"
      >
        {truncate(node.title, 21)}
      </text>
      <text
        x={x + 10}
        y={y + (isCylinder ? 36 : 34)}
        className="fill-muted-foreground font-mono text-[9px]"
      >
        {truncate(node.subtitle, 29)}
      </text>
      <circle
        cx={x + NODE_W - 12}
        cy={y + (isCylinder ? 21 : 15)}
        r={3}
        className={DOT_FILL[tone]}
      />
      <title>{`${node.title} — ${node.subtitle}`}</title>
    </g>
  );
}

function Edge({
  fromX,
  fromY,
  toX,
  toY,
}: {
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}): React.JSX.Element {
  const mid = (fromX + toX) / 2;
  const shrink = toX > fromX ? -5 : 5;
  return (
    <path
      d={`M ${fromX} ${fromY} C ${mid} ${fromY}, ${mid} ${toY}, ${toX + shrink} ${toY}`}
      fill="none"
      className="stroke-[hsl(var(--slate)/0.5)]"
      strokeWidth={1.25}
      markerEnd="url(#wr-arrow)"
    />
  );
}

function Region({
  x,
  label,
  height,
  tint,
}: {
  x: number;
  label: string;
  height: number;
  tint: 'muted' | 'none' | 'primary';
}): React.JSX.Element {
  const fill =
    tint === 'muted'
      ? 'fill-[hsl(var(--muted)/0.35)]'
      : tint === 'primary'
        ? 'fill-[hsl(var(--primary)/0.05)]'
        : 'fill-transparent';
  return (
    <g>
      <rect
        x={x - REGION_PAD}
        y={2}
        width={NODE_W + REGION_PAD * 2}
        height={height - 4}
        rx={10}
        strokeDasharray="4 4"
        className={`${fill} stroke-[hsl(var(--slate)/0.4)]`}
        strokeWidth={1}
      />
      <text
        x={x}
        y={17}
        className="fill-muted-foreground font-mono text-[9px] uppercase tracking-[0.1em]"
      >
        {label}
      </text>
    </g>
  );
}

function GraphSvg({
  workloads,
  resources,
  grants,
}: {
  workloads: Workload[];
  resources: Resource[];
  grants: Grant[];
}): React.JSX.Element {
  const left = resources.map(resourceNode);
  const mid = workloads.map(workloadNode);
  const right = grants.map(grantNode);
  const rows = Math.max(left.length, mid.length, right.length);
  const height = LABEL_H + rows * NODE_H + (rows - 1) * NODE_GAP + REGION_PAD + 4;

  // Center each column's nodes vertically within the node area below the labels.
  const colY = (count: number, i: number): number =>
    LABEL_H + ((rows - count) * (NODE_H + NODE_GAP)) / 2 + i * (NODE_H + NODE_GAP);

  return (
    <svg
      viewBox={`0 0 ${VIEW_W} ${height}`}
      // Render at natural size, never scaled up to the card width — the graph is
      // a compact summary, not a hero. It still shrinks on narrower viewports.
      className="w-full max-w-[640px]"
      role="img"
      aria-label="Graph of this app: platform resources on the left, workloads in the middle, granted integrations on the right. The same information is available as lists on small screens and to screen readers."
      data-testid="workloads-graph"
    >
      <defs>
        <marker
          id="wr-arrow"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerWidth="6"
          markerHeight="6"
          orient="auto-start-reverse"
        >
          <path d="M0,0 L10,5 L0,10 z" className="fill-[hsl(var(--slate))]" />
        </marker>
      </defs>
      {left.length > 0 && <Region x={LEFT_X} label="Resources" height={height} tint="muted" />}
      <Region x={MID_X} label="Workloads" height={height} tint="none" />
      {right.length > 0 && (
        <Region x={RIGHT_X} label="Integrations" height={height} tint="primary" />
      )}
      {mid.map((_, wi) => (
        <React.Fragment key={`edges-${wi}`}>
          {left.map((rn, ri) => (
            <Edge
              key={`el-${wi}-${rn.key}`}
              fromX={MID_X}
              fromY={colY(mid.length, wi) + NODE_H / 2}
              toX={LEFT_X + NODE_W}
              toY={colY(left.length, ri) + NODE_H / 2}
            />
          ))}
          {right.map((gn, gi) => (
            <Edge
              key={`er-${wi}-${gn.key}`}
              fromX={MID_X + NODE_W}
              fromY={colY(mid.length, wi) + NODE_H / 2}
              toX={RIGHT_X}
              toY={colY(right.length, gi) + NODE_H / 2}
            />
          ))}
        </React.Fragment>
      ))}
      {left.map((n, i) => (
        <GraphNode key={n.key} node={n} x={LEFT_X} y={colY(left.length, i)} />
      ))}
      {mid.map((n, i) => (
        <GraphNode key={n.key} node={n} x={MID_X} y={colY(mid.length, i)} />
      ))}
      {right.map((n, i) => (
        <GraphNode key={n.key} node={n} x={RIGHT_X} y={colY(right.length, i)} />
      ))}
    </svg>
  );
}

// ── Structured fallback (small screens + the screen-reader record) ───────────

function ResourceRow({ r }: { r: Resource }): React.JSX.Element {
  return (
    <li className="flex items-center justify-between py-3 text-sm">
      <div>
        <div className="font-medium">
          {r.kind} <span className="text-muted-foreground">· {r.name}</span>
        </div>
        {r.env_vars.length > 0 && (
          // The injected env-var name is the credential's reference — the handle
          // the app reads it by. The secret itself lives in the namespace's K8s
          // Secret and is never surfaced here (docs/28).
          <div className="text-xs text-muted-foreground">
            Credential injected as{' '}
            {r.env_vars.map((v) => (
              <MonoValue key={v} className="mr-1">
                {v}
              </MonoValue>
            ))}
            <span className="whitespace-nowrap">· value never shown</span>
          </div>
        )}
      </div>
      <StatusBadge variant={BADGE_VARIANT[statusTone(r.status)]}>{r.status}</StatusBadge>
    </li>
  );
}

function GrantRow({ g }: { g: Grant }): React.JSX.Element {
  return (
    <li className="flex items-center justify-between py-3 text-sm">
      <div>
        <div className="font-medium">{g.integration}</div>
        <div className="text-xs text-muted-foreground">
          credential <MonoValue>{g.credential}</MonoValue>
        </div>
      </div>
      <StatusBadge variant={BADGE_VARIANT[statusTone(g.status)]}>{g.status}</StatusBadge>
    </li>
  );
}

function StructuredLists({
  workloads,
  resources,
  grants,
}: {
  workloads: Workload[];
  resources: Resource[];
  grants: Grant[];
}): React.JSX.Element {
  return (
    <div className="space-y-4" data-testid="workloads-graph-fallback">
      {workloads.length > 0 && (
        <section aria-label="Workloads">
          <h3 className="mb-1 text-xs font-medium text-muted-foreground">Workloads</h3>
          <div className="divide-y divide-border">
            {workloads.map((w) => (
              <WorkloadRow key={w.name} workload={w} />
            ))}
          </div>
        </section>
      )}
      {resources.length > 0 && (
        <section aria-label="Resources">
          <h3 className="mb-1 text-xs font-medium text-muted-foreground">Resources</h3>
          <ul className="divide-y divide-border">
            {resources.map((r) => (
              <ResourceRow key={r.name} r={r} />
            ))}
          </ul>
        </section>
      )}
      {grants.length > 0 && (
        <section aria-label="Integrations">
          <h3 className="mb-1 text-xs font-medium text-muted-foreground">Integrations</h3>
          <ul className="divide-y divide-border">
            {grants.map((g) => (
              <GrantRow key={`${g.integration}-${g.credential}`} g={g} />
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

/**
 * "System map" — the app's connectivity at a glance (docs/28 §App
 * detail Overview): three labeled regions with the workloads in the middle,
 * platform resources to their left, granted integrations to their right.
 * Status (and a cron's last run) renders inside each node, so the graph IS the
 * surface on md+. Everything renders from the `apps.get` projection — no extra
 * reads. Below md the structured lists replace the graph; on md+ the same
 * lists stay screen-reader-only as the accessible record (and still carry
 * detail the nodes elide, like injected env-var names).
 */
export function WorkloadsResourcesGraph({ detail }: { detail: GetAppResult }): React.JSX.Element {
  const { workloads, resources, grants } = detail;
  // The graph layout needs the middle column; without workloads the structured
  // lists still surface any provisioned resources or grants (a manifest can
  // declare resources before its first workload deploys).
  const hasGraph = workloads.length > 0;
  const isEmpty = workloads.length === 0 && resources.length === 0 && grants.length === 0;

  return (
    <Card className={cn(AD_CARD, 'shadow-none')}>
      <CardHeader>
        <CardTitle>System map</CardTitle>
      </CardHeader>
      <CardContent className="text-sm">
        {isEmpty ? (
          <p className="text-muted-foreground">
            No workloads declared. Owners declare workloads and resources in{' '}
            <code>greenlight.yml</code>.
          </p>
        ) : (
          <>
            {hasGraph && (
              <div className="hidden md:block" data-testid="workloads-graph-wrap">
                <GraphSvg workloads={workloads} resources={resources} grants={grants} />
              </div>
            )}
            <div className={hasGraph ? 'md:sr-only' : undefined}>
              <StructuredLists workloads={workloads} resources={resources} grants={grants} />
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
